/**
 * Recruiter-facing "how to use" + tips copy for the Chop tools.
 *
 * One source for every surface that shows it: the web tool page
 * (`web/src/routes/research/BabyChopsV2Tool.tsx` / `ChopbookTool.tsx`), the
 * on-page panel and the toolbar popup of each extension. Before this module the strings were private
 * constants in the web page, so the extension could not show them without a
 * copy that would drift.
 */
export interface ChopGuide {
  howToSteps: readonly string[];
  tips: readonly string[];
}

export const CHOPLINK_GUIDE: ChopGuide = {
  howToSteps: [
    "Run an advanced search in LinkedIn Recruiter.",
    "The ChopLink panel appears on the results page automatically.",
    "Click Chop Data to pull the people + company data.",
    "Pick the Thrive search and click Send to NU Assist. The people list opens in NU Assist, where you can use AI on it; no download needed.",
  ],
  // Jared's advice for back-to-back chops (Trello S3iUyvzy), in his order.
  // Reworded by Diego 2026-10-14 (PR #1058); the list has no sub-headings, so
  // Diego's "If you're doing back-to-back chops" lead-in opens the first tip.
  // "chop" is lowercase mid-sentence, as everywhere else in the panel copy;
  // only button labels ("Chop Data") capitalise it.
  tips: [
    "If you're doing back-to-back chops, start each new chop in its own tab: copy the page's URL, close the tab, open a new one and paste the URL to get back to where you were, then chop again there.",
    "If you can, send the first batch to NU Assist and clear the saved data before the next chop.",
    "Starting fresh in a new tab can increase the speed because LinkedIn and chops build up data in the browser tab. A new tab frees up that wasted memory from previous chops.",
  ],
};

export const CHOPBOOK_GUIDE: ChopGuide = {
  howToSteps: [
    "Open a company or search-results page on PitchBook.",
    "The ChopBook panel appears on the page automatically.",
    // Describes the BEHAVIOUR the user will see (it is slow, leave it alone), not
    // the intent behind it. The earlier wording — "paces itself deliberately to
    // look like ordinary browsing" — was accurate to the code but put evasion
    // intent into recruiter-facing product copy, which is not something this
    // copy should be asserting on the company's behalf.
    "Pick the target search, then click Capture companies and let it run. It works through the list at a steady pace rather than all at once, so give it time.",
    // Names the SEARCH, not the Company Lists page: that page is off on prod
    // (`company-lists` in web featureFlags; `companyListsPageOn` in
    // `ChopbookPanel`), and this copy ships on every stage.
    "When it finishes, the companies are saved to that search in NU Assist.",
  ],
  // DRAFTED FROM THE CODE, not from field experience (2026-10-13) — unlike the
  // ChopLink tips these are not Jared's words yet. Each one states a behaviour
  // `ChopbookPanel` has: ticked-rows mode, the in-tab capture loop, and the
  // 10,000-row cap (`MAX_IMPORT_ROWS`). Replace them when he supplies his own.
  //
  // The LinkedIn URL tip is Diego's (2026-10-13): the column is optional, never
  // blocking, but it is what company matching compares on (`linkedin_slug`), so
  // it is asked for as a preference. Named as PitchBook names it — the capture
  // matches on that header.
  tips: [
    "If you can, add the LinkedIn URL column to your PitchBook view before capturing. It helps NU Assist match each company to the right one.",
    "Want only some companies? Tick their rows in PitchBook first. With nothing ticked, ChopBook captures every row.",
    "Keep the PitchBook tab open and stay on the page until the capture finishes.",
    "One capture holds up to 10,000 companies. For a bigger result, narrow the PitchBook search and capture it in parts.",
  ],
};
